import { useCallback, useEffect, useState } from 'react';

export type ScreenRecorderStatus =
  | 'permission-requested'
  | 'idle'
  | 'recording'
  | 'paused'
  | 'stopped'
  | 'error';

export interface UseScreenRecorderParams {
  options?: MediaRecorderOptions;
  audio?: boolean;
}

function useScreenRecorder({
  options,
  audio = false,
}: UseScreenRecorderParams) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<unknown>();
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [status, setStatus] =
    useState<ScreenRecorderStatus>('permission-requested');
  const [streams, setStreams] = useState<{
    audio: MediaStreamTrack | null;
    screen: MediaStreamTrack | null;
  }>({ audio: null, screen: null });

  useEffect(() => {
    if (!mediaRecorder) return;
    mediaRecorder.ondataavailable = (event: BlobEvent) => {
      const url = URL.createObjectURL(event.data);
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      setBlob(event.data);
    };
  }, [mediaRecorder]);

  const requestMediaStream = useCallback(async () => {
    try {
      const displayMedia = await navigator.mediaDevices.getDisplayMedia();
      let userMedia: MediaStream | undefined;
      if (audio) {
        userMedia = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      const displayTracks = displayMedia?.getTracks() ?? [];
      const userTracks = userMedia?.getTracks() ?? [];
      const tracks = [...displayTracks, ...userTracks];
      if (tracks.length) setStatus('idle');
      const stream = new MediaStream(tracks);
      const recorder = new MediaRecorder(stream, options);
      setMediaRecorder(recorder);
      setStreams({
        audio:
          userMedia?.getTracks().find((t) => t.kind === 'audio') ?? null,
        screen:
          displayMedia.getTracks().find((t) => t.kind === 'video') ?? null,
      });
      return recorder;
    } catch (e) {
      setError(e);
      setStatus('error');
      return undefined;
    }
  }, [audio, options]);

  const stopRecording = useCallback(() => {
    if (!mediaRecorder) throw new Error('No media stream!');
    mediaRecorder.stop();
    setStatus('stopped');
    mediaRecorder.stream.getTracks().forEach((track) => track.stop());
    setMediaRecorder(null);
  }, [mediaRecorder]);

  const startRecording = useCallback(async () => {
    let recorder = mediaRecorder;
    if (!recorder) {
      const next = await requestMediaStream();
      if (!next) return;
      recorder = next;
    }
    recorder.start();
    setStatus('recording');
  }, [mediaRecorder, requestMediaStream]);

  const pauseRecording = useCallback(() => {
    if (!mediaRecorder) throw new Error('No media stream!');
    mediaRecorder.pause();
    setStatus('paused');
  }, [mediaRecorder]);

  const resumeRecording = useCallback(() => {
    if (!mediaRecorder) throw new Error('No media stream!');
    mediaRecorder.resume();
    setStatus('recording');
  }, [mediaRecorder]);

  const resetRecording = useCallback(() => {
    if (mediaRecorder) {
      mediaRecorder.stream.getTracks().forEach((t) => t.stop());
    }
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setBlob(null);
    setError(undefined);
    setMediaRecorder(null);
    setStatus('idle');
    setStreams({ audio: null, screen: null });
  }, [mediaRecorder]);

  return {
    blob,
    blobUrl,
    error,
    pauseRecording,
    resetRecording,
    resumeRecording,
    startRecording,
    status,
    stopRecording,
    streams,
  };
}

export default useScreenRecorder;
